<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAdmin } from 'dashboard/composables/useAdmin';
import InboxHealthAPI from 'dashboard/api/inboxHealth';
import Button from 'next/button/Button.vue';
import Input from 'next/input/Input.vue';
import TextArea from 'next/textarea/TextArea.vue';
import Select from 'next/select/Select.vue';
import Avatar from 'next/avatar/Avatar.vue';

const props = defineProps({
  inboxId: { type: [Number, String], required: true },
});
const { t } = useI18n();
const { isAdmin } = useAdmin();
const busy = ref(false);
const loaded = ref(false);
const error = ref('');
const success = ref('');
const metadata = ref({});
const picture = ref(null);
const fileInput = ref(null);
const username = ref('');
const usernameStatus = ref('');
const suggestions = ref([]);
const original = ref({});
const form = ref({
  about: '',
  description: '',
  address: '',
  email: '',
  vertical: 'UNDEFINED',
  website1: '',
  website2: '',
});
const verticals = [
  'UNDEFINED',
  'OTHER',
  'AUTO',
  'BEAUTY',
  'APPAREL',
  'EDU',
  'ENTERTAIN',
  'EVENT_PLAN',
  'FINANCE',
  'GROCERY',
  'GOVT',
  'HOTEL',
  'HEALTH',
  'NONPROFIT',
  'PROF_SERVICES',
  'RETAIL',
  'TRAVEL',
  'RESTAURANT',
  'NOT_A_BIZ',
];
const categories = computed(() =>
  verticals.map(value => ({
    value,
    label: t(`META_PROFILE.CATEGORIES.${value}`),
  }))
);
const profilePayload = () => ({
  about: form.value.about,
  description: form.value.description,
  address: form.value.address,
  email: form.value.email,
  vertical: form.value.vertical,
  websites: [form.value.website1, form.value.website2]
    .map(url => url.trim())
    .filter(Boolean),
});
const changes = computed(() =>
  Object.fromEntries(
    Object.entries(profilePayload()).filter(
      ([key, value]) =>
        JSON.stringify(value) !== JSON.stringify(original.value[key])
    )
  )
);
const dirty = computed(
  () => loaded.value && Object.keys(changes.value).length > 0
);
const metaUrl = path => {
  const url = new URL(`https://business.facebook.com/${path}`);
  if (metadata.value.business_portfolio_id)
    url.searchParams.set('business_id', metadata.value.business_portfolio_id);
  if (metadata.value.business_account_id)
    url.searchParams.set('asset_id', metadata.value.business_account_id);
  return url.toString();
};
const managerUrl = computed(() =>
  metaUrl('latest/whatsapp_manager/phone_numbers/')
);
const billingUrl = computed(() => metaUrl('latest/billing_hub/accounts'));
const failure = e => e.response?.data?.error || t('META_PROFILE.ERROR');

const load = async () => {
  const { data } = await InboxHealthAPI.getBusinessProfile(props.inboxId);
  metadata.value = data;
  const profile = data.profile;
  form.value = {
    about: profile.about || '',
    description: profile.description || '',
    address: profile.address || '',
    email: profile.email || '',
    vertical: profile.vertical || 'UNDEFINED',
    website1: profile.websites?.[0] || '',
    website2: profile.websites?.[1] || '',
  };
  original.value = profilePayload();
  loaded.value = true;
};
const run = async action => {
  if (busy.value || !isAdmin.value) return;
  busy.value = true;
  error.value = '';
  success.value = '';
  try {
    await action();
  } catch (e) {
    error.value = failure(e);
  } finally {
    busy.value = false;
  }
};
const reloadAfterSave = async () => {
  try {
    await load();
  } catch (e) {
    error.value = t('META_PROFILE.REFRESH_ERROR');
  }
};
const save = () => {
  if (!dirty.value) return;
  run(async () => {
    await InboxHealthAPI.updateBusinessProfile(props.inboxId, changes.value);
    original.value = profilePayload();
    success.value = t('META_PROFILE.SAVED');
    await reloadAfterSave();
  });
};
const choosePicture = event => {
  picture.value = event.target.files?.[0] || null;
  if (
    picture.value &&
    (!['image/jpeg', 'image/png'].includes(picture.value.type) ||
      !picture.value.size ||
      picture.value.size > 5 * 1024 * 1024)
  ) {
    error.value = t('META_PROFILE.PICTURE_ERROR');
    picture.value = null;
    event.target.value = '';
  }
};
const savePicture = () => {
  if (!picture.value) return;
  run(async () => {
    await InboxHealthAPI.updateBusinessProfilePicture(
      props.inboxId,
      picture.value
    );
    picture.value = null;
    if (fileInput.value) fileInput.value.value = '';
    success.value = t('META_PROFILE.PICTURE_SAVED');
    await reloadAfterSave();
  });
};
const loadSuggestions = () =>
  run(async () => {
    const { data } = await InboxHealthAPI.getBusinessUsernameSuggestions(
      props.inboxId
    );
    suggestions.value = data.suggestions;
    if (!suggestions.value.length)
      success.value = t('META_PROFILE.NO_SUGGESTIONS');
  });
const requestUsername = () => {
  if (!username.value.trim()) return;
  run(async () => {
    usernameStatus.value = '';
    const { data } = await InboxHealthAPI.requestBusinessUsername(
      props.inboxId,
      username.value.trim()
    );
    usernameStatus.value = ['approved', 'reserved'].includes(data.status)
      ? t(`META_PROFILE.USERNAME_STATUS.${data.status}`)
      : t('META_PROFILE.USERNAME_STATUS.pending');
  });
};
onMounted(() => {
  if (isAdmin.value) run(load);
});
</script>

<template>
  <div v-if="isAdmin" class="mx-5 max-w-4xl space-y-5 sm:mx-6">
    <section class="rounded-xl border border-n-weak bg-n-solid-1 p-5 space-y-4">
      <div class="flex items-start justify-between gap-3">
        <div>
          <h2 class="text-heading-3 text-n-slate-12">
            {{ t('META_PROFILE.TITLE') }}
          </h2>
          <p class="mt-1 text-sm text-n-slate-11">
            {{ t('META_PROFILE.DESCRIPTION') }}
          </p>
        </div>
        <Button
          :label="t('META_PROFILE.RELOAD')"
          variant="faded"
          sm
          :disabled="busy"
          @click="run(load)"
        />
      </div>
      <p v-if="error" role="alert" class="text-sm text-n-ruby-11">
        {{ error }}
      </p>
      <p v-if="success" role="status" class="text-sm text-n-teal-11">
        {{ success }}
      </p>
      <p v-if="busy && !loaded" role="status" class="text-sm text-n-slate-11">
        {{ t('META_PROFILE.LOADING') }}
      </p>
      <template v-if="loaded">
        <div class="flex items-center gap-4">
          <Avatar
            :src="metadata.profile.profile_picture_url"
            :name="metadata.verified_name || ''"
            :size="64"
            rounded-full
          />
          <div class="min-w-0">
            <p class="text-sm font-medium text-n-slate-12">
              {{ t('META_PROFILE.PICTURE') }}
            </p>
            <p class="text-sm text-n-slate-11">
              {{ t('META_PROFILE.PICTURE_HINT') }}
            </p>
          </div>
        </div>
        <form
          class="flex flex-wrap items-center gap-3"
          @submit.prevent="savePicture"
        >
          <input
            id="meta-profile-picture"
            ref="fileInput"
            type="file"
            accept="image/jpeg,image/png"
            :aria-label="t('META_PROFILE.PICTURE')"
            :disabled="busy || dirty"
            class="max-w-full text-sm text-n-slate-11"
            @change="choosePicture"
          />
          <Button
            type="submit"
            :label="t('META_PROFILE.SAVE_PICTURE')"
            sm
            :disabled="busy || !picture || dirty"
          />
        </form>
        <form
          class="grid grid-cols-1 gap-4 sm:grid-cols-2"
          @submit.prevent="save"
        >
          <Input
            v-model="form.about"
            :label="t('META_PROFILE.ABOUT')"
            maxlength="139"
            :disabled="busy"
            class="sm:col-span-2"
          />
          <TextArea
            v-model="form.description"
            :label="t('META_PROFILE.PROFILE_DESCRIPTION')"
            :max-length="512"
            show-character-count
            :disabled="busy"
            class="sm:col-span-2"
          />
          <div>
            <p class="mb-2 text-sm font-medium text-n-slate-12">
              {{ t('META_PROFILE.CATEGORY') }}
            </p>
            <Select
              v-model="form.vertical"
              :options="categories"
              :aria-label="t('META_PROFILE.CATEGORY')"
              :disabled="busy"
            />
          </div>
          <Input
            v-model="form.email"
            type="email"
            :label="t('META_PROFILE.EMAIL')"
            maxlength="128"
            :disabled="busy"
          />
          <Input
            v-model="form.address"
            :label="t('META_PROFILE.ADDRESS')"
            maxlength="256"
            :disabled="busy"
            class="sm:col-span-2"
          />
          <Input
            v-model="form.website1"
            type="url"
            :label="t('META_PROFILE.WEBSITE1')"
            maxlength="256"
            placeholder="https://"
            :disabled="busy"
          />
          <Input
            v-model="form.website2"
            type="url"
            :label="t('META_PROFILE.WEBSITE2')"
            maxlength="256"
            placeholder="https://"
            :disabled="busy"
          />
          <p class="text-sm text-n-slate-11 sm:col-span-2">
            {{ t('META_PROFILE.SAVE_HINT') }}
          </p>
          <div class="flex gap-3 sm:col-span-2">
            <Button
              type="submit"
              :label="t('META_PROFILE.SAVE')"
              :disabled="busy || !dirty"
            />
          </div>
        </form>
      </template>
    </section>
    <section class="rounded-xl border border-n-weak bg-n-solid-1 p-5 space-y-4">
      <h3 class="text-heading-4 text-n-slate-12">
        {{ t('META_PROFILE.USERNAME') }}
      </h3>
      <p class="text-sm text-n-slate-11">
        {{ t('META_PROFILE.USERNAME_HINT') }}
      </p>
      <form
        class="flex flex-wrap items-end gap-3"
        @submit.prevent="requestUsername"
      >
        <Input
          v-model="username"
          :label="t('META_PROFILE.USERNAME_LABEL')"
          placeholder="@empresa"
          maxlength="129"
          :disabled="busy"
        />
        <Button
          type="submit"
          :label="t('META_PROFILE.REQUEST_USERNAME')"
          :disabled="busy || !username.trim()"
        />
        <Button
          type="button"
          :label="t('META_PROFILE.SUGGESTIONS')"
          variant="faded"
          :disabled="busy"
          @click="loadSuggestions"
        />
      </form>
      <div v-if="suggestions.length" class="flex flex-wrap gap-2">
        <Button
          v-for="suggestion in suggestions"
          :key="suggestion"
          :label="suggestion.startsWith('@') ? suggestion : `@${suggestion}`"
          variant="faded"
          sm
          :disabled="busy"
          @click="username = suggestion"
        />
      </div>
      <p v-if="usernameStatus" role="status" class="text-sm text-n-teal-11">
        {{ usernameStatus }}
      </p>
    </section>
    <section class="rounded-xl border border-n-weak bg-n-solid-1 p-5 space-y-4">
      <h3 class="text-heading-4 text-n-slate-12">
        {{ t('META_PROFILE.DISPLAY_NAME') }}
      </h3>
      <p v-if="metadata.verified_name" class="text-sm text-n-slate-12">
        {{ metadata.verified_name }}
      </p>
      <p class="text-sm text-n-slate-11">
        {{ t('META_PROFILE.DISPLAY_NAME_HINT') }}
      </p>
      <a
        :href="managerUrl"
        target="_blank"
        rel="noopener noreferrer"
        class="inline-flex text-sm font-medium text-n-brand"
        >{{ t('META_PROFILE.OPEN_MANAGER') }}</a
      >
      <h3 class="text-heading-4 text-n-slate-12">
        {{ t('META_PROFILE.BILLING') }}
      </h3>
      <p class="text-sm text-n-slate-11">
        {{ t('META_PROFILE.BILLING_HINT') }}
      </p>
      <a
        :href="billingUrl"
        target="_blank"
        rel="noopener noreferrer"
        class="inline-flex text-sm font-medium text-n-brand"
        >{{ t('META_PROFILE.OPEN_BILLING') }}</a
      >
    </section>
  </div>
</template>
